// Profilazione attività — T-060 rev. C.
// Form lungo navigabile a fisarmonica con tutte le domande necessarie a
// determinare COSA includere nel Manuale HACCP per questo cliente.
// Visibile solo a owner/consulente. Salva su manual_tenant_inputs.business_profile (jsonb).

import {
  IonAccordion,
  IonAccordionGroup,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { alertDialog } from '../../lib/ui/dialogs.js';
import { getSupabase } from '../../lib/supabase.js';

// ────────────────────────────────────────────────────────────────────────────
// Struttura del profilo (allineato a Zod schema business_profile)
// ────────────────────────────────────────────────────────────────────────────

interface BusinessProfile {
  identita: {
    comune?: string;
    provincia?: string;
    stagionalita?: 'annuale' | 'stagionale';
    mesi_attivita?: string;
    coperti_giorno_medi?: number;
  };
  servizi: {
    a_la_carte: boolean;
    menu_degustazione: boolean;
    buffet_colazione: boolean;
    brunch: boolean;
    aperitivo: boolean;
    bar_cocktail: boolean;
    caffetteria: boolean;
    catering: boolean;
    delivery: boolean;
    asporto: boolean;
    banchetti_eventi: boolean;
  };
  cucina: {
    pesce_fresco: boolean;
    pesce_crudo_consumo: boolean;
    // T-125: preparazioni specifiche se pesce_crudo_consumo=true. Lista chiusa.
    // Valori possibili: 'carpaccio','tartare','sashimi_style','marinato','tataki','crudo_di_mare'
    raw_fish_preparations?: string[];
    crostacei_molluschi: boolean;
    sous_vide: boolean;
    fermentazioni: boolean;
    marinature_crude: boolean;
    affumicatura: boolean;
    frittura: boolean;
    cottura_brace: boolean;
    pasta_fatta_in_casa: boolean;
    pasticceria_interna: boolean;
    gelato_fatto_in_casa: boolean;
    panificazione_interna: boolean;
    conserve_sottolio_interne: boolean;
  };
  categorie_merci: {
    carne_fresca: boolean;
    pesce_fresco: boolean;
    pesce_congelato: boolean;
    crostacei_vivi: boolean;
    molluschi_bivalvi_vivi: boolean;
    frattaglie: boolean;
    selvaggina: boolean;
    uova_fresche: boolean;
    latticini_freschi: boolean;
    ortofrutta_fresca: boolean;
    salumi: boolean;
  };
  attrezzature: {
    abbattitore: boolean;
    forno_combinato: boolean;
    n_celle_frigo?: number;
    magazzino_secco_separato: boolean;
    spogliatoio_dedicato: boolean;
    bagno_staff_separato: boolean;
    cucina_a_vista: boolean;
    acquario_vasca_live: boolean;
    sotto_vuoto: boolean;
  };
  acqua: {
    fonte?: 'acquedotto' | 'pozzo' | 'cisterna' | 'misto';
    acqua_mare_pesci_vivi: boolean;
    osmosi_addolcitore: boolean;
  };
  personale: {
    n_dipendenti_alta?: number;
    n_dipendenti_bassa?: number;
    apprendisti: boolean;
    stagisti: boolean;
    lingue_parlate?: string;
  };
  menu_speciali: {
    gluten_free_aic: boolean;
    vegetariano: boolean;
    vegano: boolean;
    bambini: boolean;
    casher: boolean;
    halal: boolean;
    intolleranti_lattosio: boolean;
  };
  contratti: {
    disinfestazione?: { ditta?: string; frequenza?: string };
    manutenzione?: { ditta?: string; frequenza?: string };
    olii_esausti?: { ditta?: string; frequenza?: string };
    rifiuti_speciali?: { ditta?: string; frequenza?: string };
  };
  strumenti: {
    termometri_digitali_n?: number;
    sonda_cuore: boolean;
    sensori_iot_temperatura: boolean;
    cassa_fiscale: boolean;
  };
  dehor: {
    has_dehor: boolean;
    posti_dehor?: number;
  };
  // ── ESTENSIONE KB-CAMPANA ───────────────────────────────────────────────
  // Sezione L: Friggitrice + parametri acrilammide (Reg. UE 2017/2158).
  // CAMPANA tratta solo punto fumo olio; TrackEAT estende con controllo
  // acrilammide via criteri di cambio olio.
  friggitrice?: {
    ha_friggitrice: boolean;
    tipo_olio?: 'girasole' | 'oliva' | 'palma' | 'arachide' | 'misto' | 'altro';
    capacita_litri?: number;
    ore_uso_giornaliere?: number;
    temperatura_max_operativa_c?: number; // default 180
    criteri_cambio_olio?: string[]; // multi: visivo_colore, schiuma_odore, n_friture, n_giorni, fry_test
    n_friture_max?: number;
    n_giorni_max?: number;
    frequenza_filtrazione?: 'mai' | 'giornaliera' | 'settimanale';
  };
  // Sezione M: Smaltimento rifiuti per categoria (oltre olii esausti).
  smaltimento_rifiuti?: {
    categorie_prodotte?: string[]; // organico, indifferenziato, carta, plastica, vetro, metalli, olii, prodotti_chimici, RAEE, RUP, cartucce
    contratti_specifici?: {
      olii_esausti?: { ditta?: string; frequenza?: string };
      raee?: { ditta?: string; frequenza?: string };
      rup_pile?: { ditta?: string };
      rifiuti_speciali_chimici?: { ditta?: string };
    };
    fir_richiesto?: boolean; // formulario rifiuti speciali
    mud_obbligatorio?: boolean; // Modello Unico Dichiarazione
  };
  // Sezione N: Pest control esteso (oltre il toggle in J).
  // CAMPANA dà 3 tipologie infestanti, 6 misure preventive, mappa trappole con codice.
  pest_control?: {
    ditta_esterna?: string;
    frequenza_interventi?: string; // es. trimestrale, mensile
    n_trappole?: number;
    tipologie_trappole?: string[]; // collanti_insetti, esche_rodenticida, lampade_uv, fero monii
    mappa_trappole_doc_path?: string; // upload planimetria con marker
    frequenza_monitoraggio_interno?: 'settimanale' | 'bisettimanale' | 'mensile';
    monitoraggio_zone?: string[]; // cucina, sala, esterno, magazzino, bagni, deposito_rifiuti
    soglia_intervento?: string; // es. "presenza anche di un solo infestante"
  };
  completed_at?: string;
}

const EMPTY_PROFILE: BusinessProfile = {
  identita: {},
  servizi: {
    a_la_carte: false,
    menu_degustazione: false,
    buffet_colazione: false,
    brunch: false,
    aperitivo: false,
    bar_cocktail: false,
    caffetteria: false,
    catering: false,
    delivery: false,
    asporto: false,
    banchetti_eventi: false,
  },
  cucina: {
    pesce_fresco: false,
    pesce_crudo_consumo: false,
    raw_fish_preparations: [],
    crostacei_molluschi: false,
    sous_vide: false,
    fermentazioni: false,
    marinature_crude: false,
    affumicatura: false,
    frittura: false,
    cottura_brace: false,
    pasta_fatta_in_casa: false,
    pasticceria_interna: false,
    gelato_fatto_in_casa: false,
    panificazione_interna: false,
    conserve_sottolio_interne: false,
  },
  categorie_merci: {
    carne_fresca: false,
    pesce_fresco: false,
    pesce_congelato: false,
    crostacei_vivi: false,
    molluschi_bivalvi_vivi: false,
    frattaglie: false,
    selvaggina: false,
    uova_fresche: false,
    latticini_freschi: false,
    ortofrutta_fresca: false,
    salumi: false,
  },
  attrezzature: {
    abbattitore: false,
    forno_combinato: false,
    magazzino_secco_separato: false,
    spogliatoio_dedicato: false,
    bagno_staff_separato: false,
    cucina_a_vista: false,
    acquario_vasca_live: false,
    sotto_vuoto: false,
  },
  acqua: {
    acqua_mare_pesci_vivi: false,
    osmosi_addolcitore: false,
  },
  personale: {
    apprendisti: false,
    stagisti: false,
  },
  menu_speciali: {
    gluten_free_aic: false,
    vegetariano: false,
    vegano: false,
    bambini: false,
    casher: false,
    halal: false,
    intolleranti_lattosio: false,
  },
  contratti: {},
  strumenti: {
    sonda_cuore: false,
    sensori_iot_temperatura: false,
    cassa_fiscale: false,
  },
  dehor: {
    has_dehor: false,
  },
  friggitrice: {
    ha_friggitrice: false,
    criteri_cambio_olio: [],
  },
  smaltimento_rifiuti: {
    categorie_prodotte: [],
    contratti_specifici: {},
    fir_richiesto: false,
    mud_obbligatorio: false,
  },
  pest_control: {
    tipologie_trappole: [],
    monitoraggio_zone: [],
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

// T-126: tipi per il catalogo attrezzature globale + l'organigramma personale
interface CatalogEntry {
  id: string;
  name: string;
  default_frequency: string;
  category: string | null;
  description: string | null;
}

interface OwnedEquipment {
  id?: string; // uuid in cleaning_equipments (undefined se non ancora salvata)
  catalog_id: string;
  marca?: string;
  modello?: string;
  numero_serie?: string;
  anno_acquisto?: number;
}

interface PersonnelAttestato {
  id?: string;
  titolo: string;
  ente?: string;
  data_rilascio?: string;
  data_scadenza?: string;
  file_storage_path?: string;
}

interface PersonnelRow {
  id?: string;
  full_name: string;
  role: string;
  formazione_attestati: PersonnelAttestato[];
  responsabilita: string[];
  attivo: boolean;
  note?: string;
}

export function BusinessProfilePage(): JSX.Element {
  const { profile: authProfile } = useAuth();
  const tenantId = authProfile?.active_tenant_id ?? null;
  const businessId = authProfile?.active_business_id ?? null;
  const role = (authProfile as any)?.role ?? 'unknown';
  const canEdit = role === 'owner' || role === 'consulente';

  const [profile, setProfile] = useState<BusinessProfile>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPersisted, setHasPersisted] = useState(false);

  // T-126 — Catalogo attrezzature globale + selezione del tenant
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [owned, setOwned] = useState<Record<string, OwnedEquipment>>({}); // catalog_id -> dettagli
  const [equipmentsDirty, setEquipmentsDirty] = useState(false);
  const [savingEquipments, setSavingEquipments] = useState(false);
  // T-126 — Organigramma personale
  const [personnel, setPersonnel] = useState<PersonnelRow[]>([]);
  const [personnelDirty, setPersonnelDirty] = useState(false);
  const [savingPersonnel, setSavingPersonnel] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from('manual_tenant_inputs')
        .select('business_profile')
        .eq('tenant_id', tenantId)
        .maybeSingle();
      if (err) throw err;
      if (data?.business_profile) {
        setProfile({ ...EMPTY_PROFILE, ...(data.business_profile as any) });
        setHasPersisted(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Carica catalogo globale + attrezzature già dichiarate dal tenant
  const fetchCatalogAndEquipments = useCallback(async () => {
    if (!tenantId) return;
    try {
      const supabase = getSupabase();
      const [catRes, eqRes] = await Promise.all([
        supabase
          .from('cleaning_equipment_catalog')
          .select('*')
          .order('default_frequency')
          .order('name'),
        supabase
          .from('cleaning_equipments')
          .select('id, catalog_id, marca, modello, numero_serie, anno_acquisto')
          .eq('tenant_id', tenantId)
          .is('archived_at', null),
      ]);
      if (catRes.error) throw catRes.error;
      if (eqRes.error) throw eqRes.error;
      setCatalog((catRes.data ?? []) as CatalogEntry[]);
      const ownedMap: Record<string, OwnedEquipment> = {};
      for (const row of (eqRes.data ?? []) as Array<{
        id: string;
        catalog_id: string | null;
        marca: string | null;
        modello: string | null;
        numero_serie: string | null;
        anno_acquisto: number | null;
      }>) {
        if (!row.catalog_id) continue;
        ownedMap[row.catalog_id] = {
          id: row.id,
          catalog_id: row.catalog_id,
          marca: row.marca ?? undefined,
          modello: row.modello ?? undefined,
          numero_serie: row.numero_serie ?? undefined,
          anno_acquisto: row.anno_acquisto ?? undefined,
        };
      }
      setOwned(ownedMap);
    } catch (e) {
      console.warn('fetchCatalogAndEquipments failed', e);
    }
  }, [tenantId]);

  // Carica organigramma del tenant
  const fetchPersonnel = useCallback(async () => {
    if (!tenantId) return;
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from('tenant_personnel')
        .select('id, full_name, role, formazione_attestati, responsabilita, attivo, note')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('full_name');
      if (err) throw err;
      interface PersonnelRowRaw {
        id: string;
        full_name: string;
        role: string;
        formazione_attestati: unknown;
        responsabilita: unknown;
        attivo: boolean | null;
        note: string | null;
      }
      const rows = ((data ?? []) as PersonnelRowRaw[]).map((r) => ({
        id: r.id,
        full_name: r.full_name,
        role: r.role,
        formazione_attestati: Array.isArray(r.formazione_attestati)
          ? (r.formazione_attestati as PersonnelAttestato[])
          : [],
        responsabilita: Array.isArray(r.responsabilita)
          ? (r.responsabilita as string[])
          : [],
        attivo: r.attivo ?? true,
        note: r.note ?? undefined,
      }));
      setPersonnel(rows);
    } catch (e) {
      console.warn('fetchPersonnel failed', e);
    }
  }, [tenantId]);

  useEffect(() => {
    void fetchProfile();
    void fetchCatalogAndEquipments();
    void fetchPersonnel();
  }, [fetchProfile, fetchCatalogAndEquipments, fetchPersonnel]);

  const save = useCallback(
    async (next: BusinessProfile) => {
      if (!tenantId || !canEdit) return;
      setSaving(true);
      try {
        const supabase = getSupabase();
        const { error: err } = await supabase
          .from('manual_tenant_inputs')
          .upsert(
            {
              tenant_id: tenantId,
              business_profile: next,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'tenant_id' },
          );
        if (err) throw err;
        setHasPersisted(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [tenantId, canEdit],
  );

  // Debounce save: salva 800ms dopo ultimo cambio
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => void save(profile), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const markCompleted = useCallback(async () => {
    const next = { ...profile, completed_at: new Date().toISOString() };
    setProfile(next);
    await save(next);
    await alertDialog('Profilo marcato come completato. Il consulente può ora generare il Manuale HACCP.', { header: 'Profilo completato' });
  }, [profile, save]);

  // T-126 — Persist attrezzature selezionate
  // Upsert delle attrezzature in `owned` con catalog_id; soft-delete (archived_at)
  // di quelle che hanno un id ma non sono più in `owned`.
  const saveEquipments = useCallback(async () => {
    if (!tenantId || !canEdit) return;
    setSavingEquipments(true);
    try {
      const supabase = getSupabase();
      // Fetch attuali per capire cosa va archiviato
      const { data: current, error: fetchErr } = await supabase
        .from('cleaning_equipments')
        .select('id, catalog_id')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .not('catalog_id', 'is', null);
      if (fetchErr) throw fetchErr;

      const currentByCatalog = new Map<string, string>();
      for (const row of (current ?? []) as Array<{ id: string; catalog_id: string | null }>) {
        if (row.catalog_id) currentByCatalog.set(row.catalog_id, row.id);
      }

      // Insert nuove + update esistenti
      for (const item of Object.values(owned)) {
        const catEntry = catalog.find((c) => c.id === item.catalog_id);
        if (!catEntry) continue;
        const payload: Record<string, unknown> = {
          tenant_id: tenantId,
          business_id: businessId,
          name: catEntry.name,
          kind: 'attrezzo',
          catalog_id: item.catalog_id,
          marca: item.marca ?? null,
          modello: item.modello ?? null,
          numero_serie: item.numero_serie ?? null,
          anno_acquisto: item.anno_acquisto ?? null,
        };
        const existingId = item.id ?? currentByCatalog.get(item.catalog_id);
        if (existingId) {
          await supabase.from('cleaning_equipments').update(payload).eq('id', existingId);
        } else if (businessId) {
          // business_id è NOT NULL — non possiamo inserire senza
          await supabase.from('cleaning_equipments').insert(payload);
        }
      }

      // Archivia attrezzature deselezionate (presenti in DB ma non più in `owned`)
      const ownedCatalogIds = new Set(Object.keys(owned));
      for (const [catalogId, eqId] of currentByCatalog.entries()) {
        if (!ownedCatalogIds.has(catalogId)) {
          await supabase
            .from('cleaning_equipments')
            .update({ archived_at: new Date().toISOString() })
            .eq('id', eqId);
        }
      }
      setEquipmentsDirty(false);
      await fetchCatalogAndEquipments();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingEquipments(false);
    }
  }, [tenantId, businessId, canEdit, owned, catalog, fetchCatalogAndEquipments]);

  // T-126 — Persist organigramma
  const savePersonnel = useCallback(async () => {
    if (!tenantId || !canEdit) return;
    setSavingPersonnel(true);
    try {
      const supabase = getSupabase();
      // Fetch attuali per capire cosa va archiviato
      const { data: current, error: fetchErr } = await supabase
        .from('tenant_personnel')
        .select('id')
        .eq('tenant_id', tenantId)
        .is('archived_at', null);
      if (fetchErr) throw fetchErr;
      const currentIds = new Set(
        (current ?? []).map((r: { id: string }) => r.id),
      );
      const localIds = new Set<string>();

      for (const row of personnel) {
        const payload: Record<string, unknown> = {
          tenant_id: tenantId,
          business_id: businessId ?? null,
          full_name: row.full_name,
          role: row.role,
          formazione_attestati: row.formazione_attestati,
          responsabilita: row.responsabilita,
          attivo: row.attivo,
          note: row.note ?? null,
        };
        if (row.id) {
          localIds.add(row.id);
          await supabase.from('tenant_personnel').update(payload).eq('id', row.id);
        } else {
          await supabase.from('tenant_personnel').insert(payload);
        }
      }

      // Archivia personale rimosso
      for (const id of currentIds) {
        if (!localIds.has(id)) {
          await supabase
            .from('tenant_personnel')
            .update({ archived_at: new Date().toISOString(), attivo: false })
            .eq('id', id);
        }
      }
      setPersonnelDirty(false);
      await fetchPersonnel();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingPersonnel(false);
    }
  }, [tenantId, businessId, canEdit, personnel, fetchPersonnel]);

  // Helper per aggiornare path tipo "servizi.bar_cocktail"
  function set<K extends keyof BusinessProfile>(
    section: K,
    key: keyof BusinessProfile[K],
    value: any,
  ) {
    setProfile((p) => ({
      ...p,
      [section]: { ...(p[section] as object), [key]: value },
    }));
  }

  // T-126 — Helper toggle attrezzatura
  function toggleEquipment(catalogId: string, checked: boolean): void {
    setEquipmentsDirty(true);
    setOwned((prev) => {
      const next = { ...prev };
      if (checked) {
        if (!next[catalogId]) {
          next[catalogId] = { catalog_id: catalogId };
        }
      } else {
        delete next[catalogId];
      }
      return next;
    });
  }

  function updateEquipmentField(
    catalogId: string,
    field: 'marca' | 'modello' | 'numero_serie' | 'anno_acquisto',
    value: string | number | undefined,
  ): void {
    setEquipmentsDirty(true);
    setOwned((prev) => {
      const cur = prev[catalogId];
      if (!cur) return prev;
      return { ...prev, [catalogId]: { ...cur, [field]: value } };
    });
  }

  // T-126 — Helper organigramma
  function addPersonnelRow(): void {
    setPersonnelDirty(true);
    setPersonnel((p) => [
      ...p,
      {
        full_name: '',
        role: 'Cuoco',
        formazione_attestati: [],
        responsabilita: [],
        attivo: true,
      },
    ]);
  }

  function updatePersonnelRow(
    idx: number,
    patch: Partial<PersonnelRow>,
  ): void {
    setPersonnelDirty(true);
    setPersonnel((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removePersonnelRow(idx: number): void {
    setPersonnelDirty(true);
    setPersonnel((p) => p.filter((_, i) => i !== idx));
  }

  if (!canEdit) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/haccp/manuale" />
            </IonButtons>
            <IonTitle>Profilazione attività</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
            Accesso riservato al consulente HACCP / titolare.
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/haccp/manuale" />
          </IonButtons>
          <IonTitle>Profilazione attività</IonTitle>
          <IonButtons slot="end">
            {saving ? (
              <IonSpinner style={{ marginRight: 12 }} />
            ) : hasPersisted ? (
              <span style={{ fontSize: 12, color: '#10b981', marginRight: 12 }}>
                Salvato
              </span>
            ) : null}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Compila le sezioni qui sotto. Le risposte determinano <strong>quali sezioni</strong>{' '}
            del Manuale HACCP verranno incluse per il tenant. Auto-salva ad ogni
            modifica. Quando hai finito, premi "Marca come completato" in fondo.
          </p>

          {loading && <IonSpinner />}
          {error && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: 12,
                color: '#b91c1c',
              }}
            >
              {error}
            </div>
          )}

          {!loading && (
            <IonAccordionGroup>
              {/* A — Identità */}
              <IonAccordion value="identita">
                <IonItem slot="header">
                  <IonLabel>A. Identità del locale</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel position="stacked">Comune</IonLabel>
                    <IonInput
                      value={profile.identita.comune ?? ''}
                      onIonInput={(e) => set('identita', 'comune', e.detail.value ?? '')}
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Provincia</IonLabel>
                    <IonInput
                      value={profile.identita.provincia ?? ''}
                      onIonInput={(e) => set('identita', 'provincia', e.detail.value ?? '')}
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Stagionalità</IonLabel>
                    <IonSelect
                      value={profile.identita.stagionalita ?? 'annuale'}
                      onIonChange={(e) =>
                        set('identita', 'stagionalita', e.detail.value)
                      }
                    >
                      <IonSelectOption value="annuale">Annuale</IonSelectOption>
                      <IonSelectOption value="stagionale">Stagionale</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                  {profile.identita.stagionalita === 'stagionale' && (
                    <IonItem>
                      <IonLabel position="stacked">
                        Mesi di apertura (es. "aprile-ottobre")
                      </IonLabel>
                      <IonInput
                        value={profile.identita.mesi_attivita ?? ''}
                        onIonInput={(e) =>
                          set('identita', 'mesi_attivita', e.detail.value ?? '')
                        }
                      />
                    </IonItem>
                  )}
                  <IonItem>
                    <IonLabel position="stacked">Coperti medi/giorno</IonLabel>
                    <IonInput
                      type="number"
                      value={profile.identita.coperti_giorno_medi ?? ''}
                      onIonInput={(e) =>
                        set(
                          'identita',
                          'coperti_giorno_medi',
                          e.detail.value ? Number(e.detail.value) : undefined,
                        )
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* B — Servizi offerti */}
              <IonAccordion value="servizi">
                <IonItem slot="header">
                  <IonLabel>B. Servizi offerti</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  {(Object.keys(profile.servizi) as Array<keyof BusinessProfile['servizi']>).map(
                    (k) => (
                      <IonItem key={k}>
                        <IonLabel>{labelServizio(k)}</IonLabel>
                        <IonToggle
                          checked={profile.servizi[k]}
                          onIonChange={(e) => set('servizi', k, e.detail.checked)}
                        />
                      </IonItem>
                    ),
                  )}
                </div>
              </IonAccordion>

              {/* C — Cucina e tecniche */}
              <IonAccordion value="cucina">
                <IonItem slot="header">
                  <IonLabel>C. Cucina e tecniche</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  {(Object.keys(profile.cucina) as Array<keyof BusinessProfile['cucina']>)
                    .filter((k) => k !== 'raw_fish_preparations')
                    .map(
                    (k) => (
                      <IonItem key={k}>
                        <IonLabel>{labelCucina(k)}</IonLabel>
                        <IonToggle
                          checked={profile.cucina[k] as boolean}
                          onIonChange={(e) => set('cucina', k, e.detail.checked)}
                        />
                      </IonItem>
                    ),
                  )}
                  {profile.cucina.pesce_crudo_consumo && (
                    <>
                      <IonNote
                        style={{
                          display: 'block',
                          padding: 12,
                          background: '#fef3c7',
                          borderRadius: 8,
                          marginTop: 8,
                          fontSize: 12,
                        }}
                      >
                        ⚠️ Pesce crudo per consumo immediato attiva il <strong>CCP-2 Abbattimento Anisakis</strong>
                        (Reg. CE 853/2004 Allegato III Sez. VIII Cap. III lett. D): -20°C × ≥24h al cuore o -35°C × ≥15h. Richiede{' '}
                        <strong>abbattitore</strong>.
                      </IonNote>
                      <div style={{ padding: 8, fontSize: 13, marginTop: 8 }}>
                        <strong>Preparazioni di pesce crudo effettivamente eseguite</strong>{' '}
                        <span style={{ color: '#666', fontWeight: 'normal' }}>
                          (almeno una è obbligatoria — finisce nel manuale al CCP-2)
                        </span>
                      </div>
                      {[
                        { v: 'carpaccio', l: 'Carpaccio' },
                        { v: 'tartare', l: 'Tartare' },
                        { v: 'sashimi_style', l: 'Sashimi-style' },
                        { v: 'marinato', l: 'Marinato (es. salmone marinato)' },
                        { v: 'tataki', l: 'Tataki (scottato esterno, crudo interno)' },
                        { v: 'crudo_di_mare', l: 'Crudo di mare (gamberi, scampi, ostriche, ricci)' },
                      ].map((opt) => (
                        <IonItem key={opt.v}>
                          <IonLabel style={{ fontSize: 13 }}>{opt.l}</IonLabel>
                          <IonToggle
                            checked={(profile.cucina.raw_fish_preparations ?? []).includes(opt.v)}
                            onIonChange={(e) => {
                              const cur = profile.cucina.raw_fish_preparations ?? [];
                              const next = e.detail.checked
                                ? Array.from(new Set([...cur, opt.v]))
                                : cur.filter((x) => x !== opt.v);
                              set('cucina', 'raw_fish_preparations', next);
                            }}
                          />
                        </IonItem>
                      ))}
                    </>
                  )}
                </div>
              </IonAccordion>

              {/* D — Categorie merceologiche */}
              <IonAccordion value="categorie_merci">
                <IonItem slot="header">
                  <IonLabel>D. Categorie merceologiche</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  {(
                    Object.keys(profile.categorie_merci) as Array<
                      keyof BusinessProfile['categorie_merci']
                    >
                  ).map((k) => (
                    <IonItem key={k}>
                      <IonLabel>{labelCategoria(k)}</IonLabel>
                      <IonToggle
                        checked={profile.categorie_merci[k]}
                        onIonChange={(e) =>
                          set('categorie_merci', k, e.detail.checked)
                        }
                      />
                    </IonItem>
                  ))}
                </div>
              </IonAccordion>

              {/* E — Attrezzature */}
              <IonAccordion value="attrezzature">
                <IonItem slot="header">
                  <IonLabel>E. Attrezzature e locali</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel>Abbattitore di temperatura</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.abbattitore}
                      onIonChange={(e) =>
                        set('attrezzature', 'abbattitore', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Forno combinato</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.forno_combinato}
                      onIonChange={(e) =>
                        set('attrezzature', 'forno_combinato', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">N. celle frigo</IonLabel>
                    <IonInput
                      type="number"
                      value={profile.attrezzature.n_celle_frigo ?? ''}
                      onIonInput={(e) =>
                        set(
                          'attrezzature',
                          'n_celle_frigo',
                          e.detail.value ? Number(e.detail.value) : undefined,
                        )
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Magazzino secco separato</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.magazzino_secco_separato}
                      onIonChange={(e) =>
                        set(
                          'attrezzature',
                          'magazzino_secco_separato',
                          e.detail.checked,
                        )
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Spogliatoio dedicato</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.spogliatoio_dedicato}
                      onIonChange={(e) =>
                        set('attrezzature', 'spogliatoio_dedicato', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Bagno staff separato</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.bagno_staff_separato}
                      onIonChange={(e) =>
                        set('attrezzature', 'bagno_staff_separato', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Cucina a vista</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.cucina_a_vista}
                      onIonChange={(e) =>
                        set('attrezzature', 'cucina_a_vista', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Acquario / vasca live (crostacei vivi)</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.acquario_vasca_live}
                      onIonChange={(e) =>
                        set('attrezzature', 'acquario_vasca_live', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Macchina per sottovuoto</IonLabel>
                    <IonToggle
                      checked={profile.attrezzature.sotto_vuoto}
                      onIonChange={(e) =>
                        set('attrezzature', 'sotto_vuoto', e.detail.checked)
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* Dehor */}
              <IonAccordion value="dehor">
                <IonItem slot="header">
                  <IonLabel>F. Dehor / terrazza</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel>Dehor / terrazza presente</IonLabel>
                    <IonToggle
                      checked={profile.dehor.has_dehor}
                      onIonChange={(e) => set('dehor', 'has_dehor', e.detail.checked)}
                    />
                  </IonItem>
                  {profile.dehor.has_dehor && (
                    <IonItem>
                      <IonLabel position="stacked">Posti dehor</IonLabel>
                      <IonInput
                        type="number"
                        value={profile.dehor.posti_dehor ?? ''}
                        onIonInput={(e) =>
                          set(
                            'dehor',
                            'posti_dehor',
                            e.detail.value ? Number(e.detail.value) : undefined,
                          )
                        }
                      />
                    </IonItem>
                  )}
                </div>
              </IonAccordion>

              {/* G — Acqua */}
              <IonAccordion value="acqua">
                <IonItem slot="header">
                  <IonLabel>G. Approvvigionamento idrico</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel position="stacked">Fonte acqua</IonLabel>
                    <IonSelect
                      value={profile.acqua.fonte ?? 'acquedotto'}
                      onIonChange={(e) => set('acqua', 'fonte', e.detail.value)}
                    >
                      <IonSelectOption value="acquedotto">Acquedotto pubblico</IonSelectOption>
                      <IonSelectOption value="pozzo">Pozzo privato</IonSelectOption>
                      <IonSelectOption value="cisterna">Cisterna</IonSelectOption>
                      <IonSelectOption value="misto">Misto</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                  <IonItem>
                    <IonLabel>Acqua di mare (per pesci/crostacei vivi)</IonLabel>
                    <IonToggle
                      checked={profile.acqua.acqua_mare_pesci_vivi}
                      onIonChange={(e) =>
                        set('acqua', 'acqua_mare_pesci_vivi', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Trattamento osmosi / addolcitore</IonLabel>
                    <IonToggle
                      checked={profile.acqua.osmosi_addolcitore}
                      onIonChange={(e) =>
                        set('acqua', 'osmosi_addolcitore', e.detail.checked)
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* H — Personale */}
              <IonAccordion value="personale">
                <IonItem slot="header">
                  <IonLabel>H. Personale</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel position="stacked">Dipendenti alta stagione</IonLabel>
                    <IonInput
                      type="number"
                      value={profile.personale.n_dipendenti_alta ?? ''}
                      onIonInput={(e) =>
                        set(
                          'personale',
                          'n_dipendenti_alta',
                          e.detail.value ? Number(e.detail.value) : undefined,
                        )
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Dipendenti bassa stagione</IonLabel>
                    <IonInput
                      type="number"
                      value={profile.personale.n_dipendenti_bassa ?? ''}
                      onIonInput={(e) =>
                        set(
                          'personale',
                          'n_dipendenti_bassa',
                          e.detail.value ? Number(e.detail.value) : undefined,
                        )
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Apprendisti</IonLabel>
                    <IonToggle
                      checked={profile.personale.apprendisti}
                      onIonChange={(e) =>
                        set('personale', 'apprendisti', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Stagisti</IonLabel>
                    <IonToggle
                      checked={profile.personale.stagisti}
                      onIonChange={(e) =>
                        set('personale', 'stagisti', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Lingue parlate dal personale</IonLabel>
                    <IonInput
                      value={profile.personale.lingue_parlate ?? ''}
                      placeholder="Italiano, Inglese, ..."
                      onIonInput={(e) =>
                        set('personale', 'lingue_parlate', e.detail.value ?? '')
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* I — Menu speciali */}
              <IonAccordion value="menu_speciali">
                <IonItem slot="header">
                  <IonLabel>I. Menù dedicati</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  {(
                    Object.keys(profile.menu_speciali) as Array<
                      keyof BusinessProfile['menu_speciali']
                    >
                  ).map((k) => (
                    <IonItem key={k}>
                      <IonLabel>{labelMenu(k)}</IonLabel>
                      <IonToggle
                        checked={profile.menu_speciali[k]}
                        onIonChange={(e) =>
                          set('menu_speciali', k, e.detail.checked)
                        }
                      />
                    </IonItem>
                  ))}
                </div>
              </IonAccordion>

              {/* J — Contratti esterni */}
              <IonAccordion value="contratti">
                <IonItem slot="header">
                  <IonLabel>J. Contratti esterni</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonLabel><h3>Disinfestazione</h3></IonLabel>
                  <IonItem>
                    <IonLabel position="stacked">Ditta</IonLabel>
                    <IonInput
                      value={profile.contratti.disinfestazione?.ditta ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          contratti: {
                            ...p.contratti,
                            disinfestazione: {
                              ...p.contratti.disinfestazione,
                              ditta: e.detail.value ?? '',
                            },
                          },
                        }))
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Frequenza</IonLabel>
                    <IonInput
                      value={profile.contratti.disinfestazione?.frequenza ?? ''}
                      placeholder="es. trimestrale"
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          contratti: {
                            ...p.contratti,
                            disinfestazione: {
                              ...p.contratti.disinfestazione,
                              frequenza: e.detail.value ?? '',
                            },
                          },
                        }))
                      }
                    />
                  </IonItem>

                  <IonLabel style={{ marginTop: 16 }}><h3>Manutenzione attrezzature</h3></IonLabel>
                  <IonItem>
                    <IonLabel position="stacked">Ditta</IonLabel>
                    <IonInput
                      value={profile.contratti.manutenzione?.ditta ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          contratti: {
                            ...p.contratti,
                            manutenzione: {
                              ...p.contratti.manutenzione,
                              ditta: e.detail.value ?? '',
                            },
                          },
                        }))
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Frequenza</IonLabel>
                    <IonInput
                      value={profile.contratti.manutenzione?.frequenza ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          contratti: {
                            ...p.contratti,
                            manutenzione: {
                              ...p.contratti.manutenzione,
                              frequenza: e.detail.value ?? '',
                            },
                          },
                        }))
                      }
                    />
                  </IonItem>

                  <IonLabel style={{ marginTop: 16 }}><h3>Smaltimento olii esausti</h3></IonLabel>
                  <IonItem>
                    <IonLabel position="stacked">Ditta</IonLabel>
                    <IonInput
                      value={profile.contratti.olii_esausti?.ditta ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          contratti: {
                            ...p.contratti,
                            olii_esausti: {
                              ...p.contratti.olii_esausti,
                              ditta: e.detail.value ?? '',
                            },
                          },
                        }))
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Frequenza</IonLabel>
                    <IonInput
                      value={profile.contratti.olii_esausti?.frequenza ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          contratti: {
                            ...p.contratti,
                            olii_esausti: {
                              ...p.contratti.olii_esausti,
                              frequenza: e.detail.value ?? '',
                            },
                          },
                        }))
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* L — Friggitrice e olii (acrilammide) */}
              <IonAccordion value="friggitrice">
                <IonItem slot="header">
                  <IonLabel>L. Friggitrice e olii (controllo acrilammide)</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel>Friggitrice presente</IonLabel>
                    <IonToggle
                      checked={!!profile.friggitrice?.ha_friggitrice}
                      onIonChange={(e) =>
                        setProfile((p) => ({
                          ...p,
                          friggitrice: { ...p.friggitrice, ha_friggitrice: e.detail.checked, criteri_cambio_olio: p.friggitrice?.criteri_cambio_olio ?? [] },
                        }))
                      }
                    />
                  </IonItem>
                  {profile.friggitrice?.ha_friggitrice && (
                    <>
                      <IonItem>
                        <IonLabel position="stacked">Tipo olio</IonLabel>
                        <IonSelect
                          value={profile.friggitrice?.tipo_olio ?? 'girasole'}
                          onIonChange={(e) =>
                            setProfile((p) => ({
                              ...p,
                              friggitrice: { ...p.friggitrice!, tipo_olio: e.detail.value },
                            }))
                          }
                        >
                          <IonSelectOption value="girasole">Girasole alto oleico</IonSelectOption>
                          <IonSelectOption value="oliva">Oliva</IonSelectOption>
                          <IonSelectOption value="arachide">Arachide</IonSelectOption>
                          <IonSelectOption value="palma">Palma</IonSelectOption>
                          <IonSelectOption value="misto">Misto</IonSelectOption>
                          <IonSelectOption value="altro">Altro</IonSelectOption>
                        </IonSelect>
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">Capacità (litri)</IonLabel>
                        <IonInput
                          type="number"
                          value={profile.friggitrice?.capacita_litri ?? ''}
                          onIonInput={(e) =>
                            setProfile((p) => ({
                              ...p,
                              friggitrice: { ...p.friggitrice!, capacita_litri: e.detail.value ? Number(e.detail.value) : undefined },
                            }))
                          }
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">Ore di utilizzo al giorno (media)</IonLabel>
                        <IonInput
                          type="number"
                          value={profile.friggitrice?.ore_uso_giornaliere ?? ''}
                          onIonInput={(e) =>
                            setProfile((p) => ({
                              ...p,
                              friggitrice: { ...p.friggitrice!, ore_uso_giornaliere: e.detail.value ? Number(e.detail.value) : undefined },
                            }))
                          }
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">T° massima operativa (°C) — limite consigliato 180 per controllo acrilammide</IonLabel>
                        <IonInput
                          type="number"
                          value={profile.friggitrice?.temperatura_max_operativa_c ?? 180}
                          onIonInput={(e) =>
                            setProfile((p) => ({
                              ...p,
                              friggitrice: { ...p.friggitrice!, temperatura_max_operativa_c: e.detail.value ? Number(e.detail.value) : 180 },
                            }))
                          }
                        />
                      </IonItem>
                      <div style={{ padding: 8 }}>
                        <strong>Criteri di cambio olio (multi-scelta)</strong>
                      </div>
                      {[
                        { v: 'visivo_colore', l: 'Cambio visivo per colore (brunito scuro)' },
                        { v: 'visivo_schiuma', l: 'Cambio visivo per schiuma/odore' },
                        { v: 'n_friture', l: 'Dopo N fritture' },
                        { v: 'n_giorni', l: 'Dopo N giorni' },
                        { v: 'fry_test', l: 'Tramite test strumentale (TPM/FOS)' },
                      ].map((opt) => (
                        <IonItem key={opt.v}>
                          <IonLabel style={{ fontSize: 13 }}>{opt.l}</IonLabel>
                          <IonToggle
                            checked={(profile.friggitrice?.criteri_cambio_olio ?? []).includes(opt.v)}
                            onIonChange={(e) => {
                              const cur = profile.friggitrice?.criteri_cambio_olio ?? [];
                              const next = e.detail.checked
                                ? [...cur, opt.v]
                                : cur.filter((x) => x !== opt.v);
                              setProfile((p) => ({
                                ...p,
                                friggitrice: { ...p.friggitrice!, criteri_cambio_olio: next },
                              }));
                            }}
                          />
                        </IonItem>
                      ))}
                      {(profile.friggitrice?.criteri_cambio_olio ?? []).includes('n_friture') && (
                        <IonItem>
                          <IonLabel position="stacked">N. fritture massimo prima del cambio</IonLabel>
                          <IonInput
                            type="number"
                            value={profile.friggitrice?.n_friture_max ?? ''}
                            onIonInput={(e) =>
                              setProfile((p) => ({
                                ...p,
                                friggitrice: { ...p.friggitrice!, n_friture_max: e.detail.value ? Number(e.detail.value) : undefined },
                              }))
                            }
                          />
                        </IonItem>
                      )}
                      {(profile.friggitrice?.criteri_cambio_olio ?? []).includes('n_giorni') && (
                        <IonItem>
                          <IonLabel position="stacked">N. giorni massimo prima del cambio</IonLabel>
                          <IonInput
                            type="number"
                            value={profile.friggitrice?.n_giorni_max ?? ''}
                            onIonInput={(e) =>
                              setProfile((p) => ({
                                ...p,
                                friggitrice: { ...p.friggitrice!, n_giorni_max: e.detail.value ? Number(e.detail.value) : undefined },
                              }))
                            }
                          />
                        </IonItem>
                      )}
                      <IonItem>
                        <IonLabel position="stacked">Frequenza filtrazione olio</IonLabel>
                        <IonSelect
                          value={profile.friggitrice?.frequenza_filtrazione ?? 'mai'}
                          onIonChange={(e) =>
                            setProfile((p) => ({
                              ...p,
                              friggitrice: { ...p.friggitrice!, frequenza_filtrazione: e.detail.value },
                            }))
                          }
                        >
                          <IonSelectOption value="mai">Mai</IonSelectOption>
                          <IonSelectOption value="giornaliera">Giornaliera</IonSelectOption>
                          <IonSelectOption value="settimanale">Settimanale</IonSelectOption>
                        </IonSelect>
                      </IonItem>
                      <IonNote
                        style={{
                          display: 'block',
                          padding: 12,
                          background: '#fef3c7',
                          borderRadius: 8,
                          marginTop: 8,
                          fontSize: 12,
                        }}
                      >
                        ⚠️ <strong>Acrilammide (Reg. UE 2017/2158)</strong>: temperature
                        elevate e cotture prolungate aumentano il rischio. Il manuale
                        generato includerà la procedura di controllo basata sui criteri
                        sopra indicati.
                      </IonNote>
                    </>
                  )}
                </div>
              </IonAccordion>

              {/* M — Smaltimento rifiuti */}
              <IonAccordion value="smaltimento">
                <IonItem slot="header">
                  <IonLabel>M. Smaltimento rifiuti per categoria</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <div style={{ padding: 8, fontSize: 13, color: '#666' }}>
                    Categorie di rifiuti prodotti dall'attività (multi-scelta).
                  </div>
                  {[
                    { v: 'organico', l: 'Organico (FORSU)' },
                    { v: 'indifferenziato', l: 'Indifferenziato' },
                    { v: 'carta_cartone', l: 'Carta e cartone' },
                    { v: 'plastica', l: 'Plastica' },
                    { v: 'vetro', l: 'Vetro' },
                    { v: 'metalli', l: 'Metalli (lattine, banda stagnata)' },
                    { v: 'olii_esausti', l: 'Olii esausti (CER 200125)' },
                    { v: 'prodotti_chimici', l: 'Prodotti chimici pulizia (CER 200129*)' },
                    { v: 'raee', l: 'RAEE (apparecchi elettrici/elettronici)' },
                    { v: 'rup_pile', l: 'RUP pile / batterie' },
                    { v: 'cartucce_toner', l: 'Cartucce / toner stampante' },
                    { v: 'imballaggi_industriali', l: 'Imballaggi industriali (es. pellicole + cassette polistirolo del pesce)' },
                  ].map((opt) => (
                    <IonItem key={opt.v}>
                      <IonLabel style={{ fontSize: 13 }}>{opt.l}</IonLabel>
                      <IonToggle
                        checked={(profile.smaltimento_rifiuti?.categorie_prodotte ?? []).includes(opt.v)}
                        onIonChange={(e) => {
                          const cur = profile.smaltimento_rifiuti?.categorie_prodotte ?? [];
                          const next = e.detail.checked
                            ? [...cur, opt.v]
                            : cur.filter((x) => x !== opt.v);
                          setProfile((p) => ({
                            ...p,
                            smaltimento_rifiuti: { ...p.smaltimento_rifiuti!, categorie_prodotte: next },
                          }));
                        }}
                      />
                    </IonItem>
                  ))}
                  <IonItem>
                    <IonLabel>FIR — Formulario rifiuti speciali richiesto</IonLabel>
                    <IonToggle
                      checked={!!profile.smaltimento_rifiuti?.fir_richiesto}
                      onIonChange={(e) =>
                        setProfile((p) => ({
                          ...p,
                          smaltimento_rifiuti: { ...p.smaltimento_rifiuti!, fir_richiesto: e.detail.checked },
                        }))
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>MUD — Modello Unico Dichiarazione obbligatorio</IonLabel>
                    <IonToggle
                      checked={!!profile.smaltimento_rifiuti?.mud_obbligatorio}
                      onIonChange={(e) =>
                        setProfile((p) => ({
                          ...p,
                          smaltimento_rifiuti: { ...p.smaltimento_rifiuti!, mud_obbligatorio: e.detail.checked },
                        }))
                      }
                    />
                  </IonItem>
                  <div style={{ padding: 8, fontSize: 12, color: '#666' }}>
                    I contratti specifici (ditta + frequenza) per olii esausti,
                    RAEE, RUP, rifiuti chimici sono nella sezione <strong>J. Contratti
                    esterni</strong> sopra.
                  </div>
                </div>
              </IonAccordion>

              {/* N — Pest control esteso */}
              <IonAccordion value="pest_control">
                <IonItem slot="header">
                  <IonLabel>N. Pest control — monitoraggio e trappole</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel position="stacked">Ditta esterna di disinfestazione</IonLabel>
                    <IonInput
                      value={profile.pest_control?.ditta_esterna ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          pest_control: { ...p.pest_control!, ditta_esterna: e.detail.value ?? '' },
                        }))
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Frequenza interventi ditta</IonLabel>
                    <IonInput
                      placeholder="es. trimestrale, mensile, semestrale"
                      value={profile.pest_control?.frequenza_interventi ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          pest_control: { ...p.pest_control!, frequenza_interventi: e.detail.value ?? '' },
                        }))
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position="stacked">Numero trappole installate (totale)</IonLabel>
                    <IonInput
                      type="number"
                      value={profile.pest_control?.n_trappole ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          pest_control: { ...p.pest_control!, n_trappole: e.detail.value ? Number(e.detail.value) : undefined },
                        }))
                      }
                    />
                  </IonItem>
                  <div style={{ padding: 8, fontSize: 13 }}>
                    <strong>Tipologie trappole installate</strong>
                  </div>
                  {[
                    { v: 'collanti_insetti', l: 'Collanti per insetti' },
                    { v: 'esche_rodenticida', l: 'Esche rodenticida (topi/ratti)' },
                    { v: 'lampade_uv', l: 'Lampade UV insetto-attrattive' },
                    { v: 'feromoni', l: 'Trappole a feromoni (tarme)' },
                    { v: 'meccaniche', l: 'Trappole meccaniche (a scatto)' },
                  ].map((opt) => (
                    <IonItem key={opt.v}>
                      <IonLabel style={{ fontSize: 13 }}>{opt.l}</IonLabel>
                      <IonToggle
                        checked={(profile.pest_control?.tipologie_trappole ?? []).includes(opt.v)}
                        onIonChange={(e) => {
                          const cur = profile.pest_control?.tipologie_trappole ?? [];
                          const next = e.detail.checked
                            ? [...cur, opt.v]
                            : cur.filter((x) => x !== opt.v);
                          setProfile((p) => ({
                            ...p,
                            pest_control: { ...p.pest_control!, tipologie_trappole: next },
                          }));
                        }}
                      />
                    </IonItem>
                  ))}
                  <IonItem>
                    <IonLabel position="stacked">Frequenza monitoraggio interno</IonLabel>
                    <IonSelect
                      value={profile.pest_control?.frequenza_monitoraggio_interno ?? 'settimanale'}
                      onIonChange={(e) =>
                        setProfile((p) => ({
                          ...p,
                          pest_control: { ...p.pest_control!, frequenza_monitoraggio_interno: e.detail.value },
                        }))
                      }
                    >
                      <IonSelectOption value="settimanale">Settimanale</IonSelectOption>
                      <IonSelectOption value="bisettimanale">Bisettimanale</IonSelectOption>
                      <IonSelectOption value="mensile">Mensile</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                  <div style={{ padding: 8, fontSize: 13 }}>
                    <strong>Zone monitorate</strong>
                  </div>
                  {['cucina', 'sala', 'magazzino', 'esterno', 'bagni', 'deposito_rifiuti'].map((z) => (
                    <IonItem key={z}>
                      <IonLabel style={{ fontSize: 13, textTransform: 'capitalize' }}>{z.replace('_', ' ')}</IonLabel>
                      <IonToggle
                        checked={(profile.pest_control?.monitoraggio_zone ?? []).includes(z)}
                        onIonChange={(e) => {
                          const cur = profile.pest_control?.monitoraggio_zone ?? [];
                          const next = e.detail.checked ? [...cur, z] : cur.filter((x) => x !== z);
                          setProfile((p) => ({
                            ...p,
                            pest_control: { ...p.pest_control!, monitoraggio_zone: next },
                          }));
                        }}
                      />
                    </IonItem>
                  ))}
                  <IonItem>
                    <IonLabel position="stacked">Soglia di intervento</IonLabel>
                    <IonTextarea
                      placeholder="es. 'Presenza anche di un solo infestante visivo o tracce — chiamata immediata ditta esterna'"
                      rows={2}
                      value={profile.pest_control?.soglia_intervento ?? ''}
                      onIonInput={(e) =>
                        setProfile((p) => ({
                          ...p,
                          pest_control: { ...p.pest_control!, soglia_intervento: e.detail.value ?? '' },
                        }))
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* K — Strumenti */}
              <IonAccordion value="strumenti">
                <IonItem slot="header">
                  <IonLabel>K. Strumentazione</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <IonItem>
                    <IonLabel position="stacked">N. termometri digitali</IonLabel>
                    <IonInput
                      type="number"
                      value={profile.strumenti.termometri_digitali_n ?? ''}
                      onIonInput={(e) =>
                        set(
                          'strumenti',
                          'termometri_digitali_n',
                          e.detail.value ? Number(e.detail.value) : undefined,
                        )
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Sonda al cuore</IonLabel>
                    <IonToggle
                      checked={profile.strumenti.sonda_cuore}
                      onIonChange={(e) =>
                        set('strumenti', 'sonda_cuore', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Sensori IoT temperature</IonLabel>
                    <IonToggle
                      checked={profile.strumenti.sensori_iot_temperatura}
                      onIonChange={(e) =>
                        set('strumenti', 'sensori_iot_temperatura', e.detail.checked)
                      }
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Cassa fiscale</IonLabel>
                    <IonToggle
                      checked={profile.strumenti.cassa_fiscale}
                      onIonChange={(e) =>
                        set('strumenti', 'cassa_fiscale', e.detail.checked)
                      }
                    />
                  </IonItem>
                </div>
              </IonAccordion>

              {/* P — Attrezzature dettagliate (catalogo standard) — T-126 */}
              <IonAccordion value="attrezzature_catalogo">
                <IonItem slot="header">
                  <IonLabel>P. Attrezzature dettagliate (catalogo)</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <div style={{ padding: 8, fontSize: 13, color: '#666' }}>
                    Per ogni attrezzatura presente nel locale, attiva la voce e
                    compila marca / modello / numero di serie / anno di acquisto.
                    La lista finisce nel <strong>Manuale HACCP — Allegato 02</strong>
                    {' '}per l'ispezione ASL (Decreto Assessoriale Sanità Sicilia 13/12/2012).
                  </div>
                  {(['giornaliera', 'settimanale', 'mensile'] as const).map(
                    (freq) => {
                      const items = catalog.filter(
                        (c) => c.default_frequency === freq,
                      );
                      if (items.length === 0) return null;
                      const freqLabel =
                        freq === 'giornaliera'
                          ? 'Pulizia giornaliera'
                          : freq === 'settimanale'
                            ? 'Pulizia settimanale'
                            : 'Pulizia mensile';
                      return (
                        <div key={freq} style={{ marginTop: 12 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 13,
                              padding: '8px 8px 4px',
                              color: '#0f172a',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}
                          >
                            {freqLabel}
                          </div>
                          {items.map((c) => {
                            const selected = !!owned[c.id];
                            return (
                              <div
                                key={c.id}
                                style={{
                                  background: selected ? '#f0f9ff' : '#fff',
                                  borderLeft: selected
                                    ? '3px solid #0284c7'
                                    : '3px solid transparent',
                                  marginBottom: 4,
                                }}
                              >
                                <IonItem lines="full">
                                  <IonLabel>
                                    <h3 style={{ fontSize: 14 }}>{c.name}</h3>
                                    {c.description && (
                                      <p
                                        style={{
                                          fontSize: 11,
                                          color: '#64748b',
                                          marginTop: 2,
                                        }}
                                      >
                                        {c.description}
                                      </p>
                                    )}
                                  </IonLabel>
                                  <IonToggle
                                    checked={selected}
                                    onIonChange={(e) =>
                                      toggleEquipment(c.id, e.detail.checked)
                                    }
                                  />
                                </IonItem>
                                {selected && (
                                  <div
                                    style={{ padding: '0 16px 12px', background: '#f8fafc' }}
                                  >
                                    <IonItem lines="none">
                                      <IonLabel position="stacked">Marca</IonLabel>
                                      <IonInput
                                        placeholder="es. Electrolux"
                                        value={owned[c.id]?.marca ?? ''}
                                        onIonInput={(e) =>
                                          updateEquipmentField(
                                            c.id,
                                            'marca',
                                            e.detail.value ?? '',
                                          )
                                        }
                                      />
                                    </IonItem>
                                    <IonItem lines="none">
                                      <IonLabel position="stacked">Modello</IonLabel>
                                      <IonInput
                                        placeholder="es. Pratica 600"
                                        value={owned[c.id]?.modello ?? ''}
                                        onIonInput={(e) =>
                                          updateEquipmentField(
                                            c.id,
                                            'modello',
                                            e.detail.value ?? '',
                                          )
                                        }
                                      />
                                    </IonItem>
                                    <IonItem lines="none">
                                      <IonLabel position="stacked">Numero di serie</IonLabel>
                                      <IonInput
                                        placeholder="placca metallica"
                                        value={owned[c.id]?.numero_serie ?? ''}
                                        onIonInput={(e) =>
                                          updateEquipmentField(
                                            c.id,
                                            'numero_serie',
                                            e.detail.value ?? '',
                                          )
                                        }
                                      />
                                    </IonItem>
                                    <IonItem lines="none">
                                      <IonLabel position="stacked">Anno di acquisto</IonLabel>
                                      <IonInput
                                        type="number"
                                        placeholder="es. 2024"
                                        value={owned[c.id]?.anno_acquisto ?? ''}
                                        onIonInput={(e) =>
                                          updateEquipmentField(
                                            c.id,
                                            'anno_acquisto',
                                            e.detail.value
                                              ? Number(e.detail.value)
                                              : undefined,
                                          )
                                        }
                                      />
                                    </IonItem>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    },
                  )}
                  <div style={{ padding: 12 }}>
                    <IonButton
                      expand="block"
                      disabled={!equipmentsDirty || savingEquipments || !businessId}
                      onClick={() => void saveEquipments()}
                    >
                      {savingEquipments
                        ? 'Salvataggio...'
                        : equipmentsDirty
                          ? 'Salva attrezzature'
                          : 'Salvato'}
                    </IonButton>
                    {!businessId && (
                      <IonNote
                        style={{
                          display: 'block',
                          padding: 8,
                          color: '#b45309',
                          fontSize: 12,
                        }}
                      >
                        ⚠️ Selezionare un'attività operativa (business) prima di
                        salvare le attrezzature.
                      </IonNote>
                    )}
                  </div>
                </div>
              </IonAccordion>

              {/* Q — Organigramma personale — T-126 */}
              <IonAccordion value="organigramma">
                <IonItem slot="header">
                  <IonLabel>Q. Organigramma personale</IonLabel>
                </IonItem>
                <div className="ion-padding" slot="content">
                  <div style={{ padding: 8, fontSize: 13, color: '#666' }}>
                    Anagrafica completa di tutto il personale del locale
                    (titolare, responsabile HACCP, cuochi, camerieri, lavapiatti,
                    addetti pulizie). Confluisce nel <strong>Manuale HACCP CAP 1.5 bis</strong>
                    {' '}(Organigramma) e nel piano formazione MOD-05.
                  </div>
                  {personnel.length === 0 ? (
                    <IonNote
                      style={{
                        display: 'block',
                        padding: 16,
                        color: '#64748b',
                        fontStyle: 'italic',
                        fontSize: 13,
                        textAlign: 'center',
                      }}
                    >
                      Nessuna persona registrata. Aggiungi almeno il titolare e il
                      responsabile HACCP.
                    </IonNote>
                  ) : (
                    personnel.map((row, idx) => (
                      <div
                        key={row.id ?? `new-${idx}`}
                        style={{
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          padding: 12,
                          margin: '8px 0',
                        }}
                      >
                        <IonItem lines="none">
                          <IonLabel position="stacked">Nome completo</IonLabel>
                          <IonInput
                            placeholder="es. Mario Rossi"
                            value={row.full_name}
                            onIonInput={(e) =>
                              updatePersonnelRow(idx, {
                                full_name: e.detail.value ?? '',
                              })
                            }
                          />
                        </IonItem>
                        <IonItem lines="none">
                          <IonLabel position="stacked">Ruolo</IonLabel>
                          <IonSelect
                            value={row.role}
                            onIonChange={(e) =>
                              updatePersonnelRow(idx, { role: e.detail.value })
                            }
                          >
                            <IonSelectOption value="Titolare">Titolare</IonSelectOption>
                            <IonSelectOption value="Responsabile HACCP">
                              Responsabile HACCP
                            </IonSelectOption>
                            <IonSelectOption value="Cuoco capo">Cuoco capo</IonSelectOption>
                            <IonSelectOption value="Cuoco">Cuoco</IonSelectOption>
                            <IonSelectOption value="Sous-chef">Sous-chef</IonSelectOption>
                            <IonSelectOption value="Pizzaiolo">Pizzaiolo</IonSelectOption>
                            <IonSelectOption value="Pasticcere">Pasticcere</IonSelectOption>
                            <IonSelectOption value="Aiuto cucina">Aiuto cucina</IonSelectOption>
                            <IonSelectOption value="Cameriere">Cameriere</IonSelectOption>
                            <IonSelectOption value="Barista">Barista</IonSelectOption>
                            <IonSelectOption value="Lavapiatti">Lavapiatti</IonSelectOption>
                            <IonSelectOption value="Addetto pulizie">
                              Addetto pulizie
                            </IonSelectOption>
                            <IonSelectOption value="Magazziniere">Magazziniere</IonSelectOption>
                            <IonSelectOption value="Altro">Altro</IonSelectOption>
                          </IonSelect>
                        </IonItem>
                        <div style={{ padding: '8px 16px', fontSize: 12 }}>
                          <strong>Responsabilità</strong>
                        </div>
                        {[
                          { v: 'materie_prime', l: 'Ricezione materie prime' },
                          { v: 'procedure', l: 'Definizione procedure' },
                          { v: 'temperature', l: 'Controllo temperature' },
                          { v: 'pulizie', l: 'Sanificazione' },
                          { v: 'documentazione', l: 'Documentazione' },
                          { v: 'formazione_staff', l: 'Formazione staff' },
                          { v: 'allergeni', l: 'Gestione allergeni' },
                          { v: 'tracciabilita', l: 'Tracciabilità' },
                        ].map((opt) => (
                          <IonItem key={opt.v} lines="none">
                            <IonLabel style={{ fontSize: 12 }}>{opt.l}</IonLabel>
                            <IonToggle
                              checked={row.responsabilita.includes(opt.v)}
                              onIonChange={(e) => {
                                const next = e.detail.checked
                                  ? Array.from(
                                      new Set([...row.responsabilita, opt.v]),
                                    )
                                  : row.responsabilita.filter((x) => x !== opt.v);
                                updatePersonnelRow(idx, { responsabilita: next });
                              }}
                            />
                          </IonItem>
                        ))}
                        <div style={{ padding: '8px 16px', fontSize: 12, marginTop: 4 }}>
                          <strong>Attestati di formazione</strong>
                        </div>
                        {row.formazione_attestati.length === 0 ? (
                          <IonNote
                            style={{
                              display: 'block',
                              padding: '4px 16px',
                              color: '#64748b',
                              fontStyle: 'italic',
                              fontSize: 11,
                            }}
                          >
                            Nessun attestato registrato.
                          </IonNote>
                        ) : (
                          row.formazione_attestati.map((att, attIdx) => (
                            <div
                              key={attIdx}
                              style={{
                                padding: '4px 16px',
                                background: '#f8fafc',
                                borderRadius: 6,
                                margin: '4px 8px',
                              }}
                            >
                              <IonItem lines="none">
                                <IonLabel position="stacked">Titolo attestato</IonLabel>
                                <IonInput
                                  placeholder="es. HACCP Categoria A"
                                  value={att.titolo}
                                  onIonInput={(e) => {
                                    const list = [...row.formazione_attestati];
                                    list[attIdx] = {
                                      ...list[attIdx],
                                      titolo: e.detail.value ?? '',
                                    };
                                    updatePersonnelRow(idx, {
                                      formazione_attestati: list,
                                    });
                                  }}
                                />
                              </IonItem>
                              <IonItem lines="none">
                                <IonLabel position="stacked">Ente rilascio</IonLabel>
                                <IonInput
                                  placeholder="es. ASP Agrigento"
                                  value={att.ente ?? ''}
                                  onIonInput={(e) => {
                                    const list = [...row.formazione_attestati];
                                    list[attIdx] = {
                                      ...list[attIdx],
                                      ente: e.detail.value ?? '',
                                    };
                                    updatePersonnelRow(idx, {
                                      formazione_attestati: list,
                                    });
                                  }}
                                />
                              </IonItem>
                              <IonItem lines="none">
                                <IonLabel position="stacked">Data rilascio</IonLabel>
                                <IonInput
                                  type="date"
                                  value={att.data_rilascio ?? ''}
                                  onIonInput={(e) => {
                                    const list = [...row.formazione_attestati];
                                    list[attIdx] = {
                                      ...list[attIdx],
                                      data_rilascio: e.detail.value ?? '',
                                    };
                                    updatePersonnelRow(idx, {
                                      formazione_attestati: list,
                                    });
                                  }}
                                />
                              </IonItem>
                              <IonItem lines="none">
                                <IonLabel position="stacked">Data scadenza</IonLabel>
                                <IonInput
                                  type="date"
                                  value={att.data_scadenza ?? ''}
                                  onIonInput={(e) => {
                                    const list = [...row.formazione_attestati];
                                    list[attIdx] = {
                                      ...list[attIdx],
                                      data_scadenza: e.detail.value ?? '',
                                    };
                                    updatePersonnelRow(idx, {
                                      formazione_attestati: list,
                                    });
                                  }}
                                />
                              </IonItem>
                              {att.file_storage_path ? (
                                <IonNote
                                  style={{
                                    display: 'block',
                                    padding: '8px 16px',
                                    color: '#166534',
                                    fontWeight: 600,
                                  }}
                                >
                                  PDF ufficiale archiviato. Gestiscilo da Registri →
                                  Formazione personale.
                                </IonNote>
                              ) : (
                                <IonButton
                                  fill="clear"
                                  size="small"
                                  color="danger"
                                  onClick={() => {
                                    const list = row.formazione_attestati.filter(
                                      (_, i) => i !== attIdx,
                                    );
                                    updatePersonnelRow(idx, {
                                      formazione_attestati: list,
                                    });
                                  }}
                                >
                                  Rimuovi attestato
                                </IonButton>
                              )}
                            </div>
                          ))
                        )}
                        <div style={{ padding: '4px 16px' }}>
                          <IonButton
                            fill="outline"
                            size="small"
                            onClick={() =>
                              updatePersonnelRow(idx, {
                                formazione_attestati: [
                                  ...row.formazione_attestati,
                                  { titolo: '' },
                                ],
                              })
                            }
                          >
                            + Aggiungi attestato
                          </IonButton>
                        </div>
                        <div
                          style={{
                            padding: 12,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <IonItem
                            lines="none"
                            style={{ '--padding-start': 0 } as React.CSSProperties}
                          >
                            <IonLabel style={{ fontSize: 12 }}>In servizio</IonLabel>
                            <IonToggle
                              checked={row.attivo}
                              onIonChange={(e) =>
                                updatePersonnelRow(idx, { attivo: e.detail.checked })
                              }
                            />
                          </IonItem>
                          <IonButton
                            fill="clear"
                            color="danger"
                            size="small"
                            onClick={() => removePersonnelRow(idx)}
                          >
                            Rimuovi persona
                          </IonButton>
                        </div>
                      </div>
                    ))
                  )}
                  <div style={{ padding: 12, display: 'flex', gap: 8 }}>
                    <IonButton
                      fill="outline"
                      expand="block"
                      onClick={() => addPersonnelRow()}
                    >
                      + Aggiungi persona
                    </IonButton>
                    <IonButton
                      expand="block"
                      disabled={!personnelDirty || savingPersonnel}
                      onClick={() => void savePersonnel()}
                    >
                      {savingPersonnel
                        ? 'Salvataggio...'
                        : personnelDirty
                          ? 'Salva organigramma'
                          : 'Salvato'}
                    </IonButton>
                  </div>
                </div>
              </IonAccordion>
            </IonAccordionGroup>
          )}

          {!loading && (
            <div style={{ marginTop: 24 }}>
              {profile.completed_at ? (
                <div
                  style={{
                    padding: 12,
                    background: '#dcfce7',
                    color: '#166534',
                    borderRadius: 8,
                    textAlign: 'center',
                  }}
                >
                  ✅ Profilo completato il{' '}
                  {new Date(profile.completed_at).toLocaleString('it-IT')}
                </div>
              ) : (
                <IonButton expand="block" onClick={() => void markCompleted()}>
                  Marca come completato
                </IonButton>
              )}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Labels italiani
// ────────────────────────────────────────────────────────────────────────────

function labelServizio(k: string): string {
  const m: Record<string, string> = {
    a_la_carte: 'Ristorazione à la carte',
    menu_degustazione: 'Menù degustazione',
    buffet_colazione: 'Buffet colazione',
    brunch: 'Brunch',
    aperitivo: 'Aperitivo',
    bar_cocktail: 'Bar / cocktail',
    caffetteria: 'Caffetteria',
    catering: 'Catering presso terzi',
    delivery: 'Delivery a domicilio',
    asporto: 'Asporto / take-away',
    banchetti_eventi: 'Banchetti / eventi privati',
  };
  return m[k] ?? k;
}

function labelCucina(k: string): string {
  const m: Record<string, string> = {
    pesce_fresco: 'Pesce fresco',
    pesce_crudo_consumo: 'Pesce crudo per consumo immediato (sashimi, tartare, ceviche, marinati)',
    crostacei_molluschi: 'Crostacei e molluschi',
    sous_vide: 'Sous-vide / cottura sottovuoto a bassa temperatura',
    fermentazioni: 'Fermentazioni',
    marinature_crude: 'Marinature crude (es. salmone marinato)',
    affumicatura: 'Affumicatura interna',
    frittura: 'Frittura',
    cottura_brace: 'Cottura alla brace',
    pasta_fatta_in_casa: 'Pasta fatta in casa',
    pasticceria_interna: 'Pasticceria interna (dolci, biscotti)',
    gelato_fatto_in_casa: 'Gelato fatto in casa',
    panificazione_interna: 'Panificazione interna',
    conserve_sottolio_interne: "Conserve sott'olio fatte in casa",
  };
  return m[k] ?? k;
}

function labelCategoria(k: string): string {
  const m: Record<string, string> = {
    carne_fresca: 'Carne fresca',
    pesce_fresco: 'Pesce fresco',
    pesce_congelato: 'Pesce congelato',
    crostacei_vivi: 'Crostacei vivi',
    molluschi_bivalvi_vivi: 'Molluschi bivalvi vivi (cozze, vongole, ostriche)',
    frattaglie: 'Frattaglie',
    selvaggina: 'Selvaggina',
    uova_fresche: 'Uova fresche',
    latticini_freschi: 'Latticini freschi',
    ortofrutta_fresca: 'Ortofrutta fresca',
    salumi: 'Salumi',
  };
  return m[k] ?? k;
}

function labelMenu(k: string): string {
  const m: Record<string, string> = {
    gluten_free_aic: 'Gluten-free certificato AIC',
    vegetariano: 'Menù vegetariano',
    vegano: 'Menù vegano',
    bambini: 'Menù bambini',
    casher: 'Cibo casher',
    halal: 'Cibo halal',
    intolleranti_lattosio: 'Opzioni senza lattosio',
  };
  return m[k] ?? k;
}
